import { useEffect, useState } from 'react';
import type { ServiceType } from '@common/enums/serviceType';
import {
    AdminTable,
    EditButton,
    FullName,
    Header,
    Link,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '@frontend/components/elements/common';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { NullableFormattedDate } from '../../elements/NullableFormattedDate';

interface IServicesConsumingListProps {
    id: string;
}

interface IService {
    id: string;
    name: string;
    serviceType: ServiceType;
}

interface IServiceConsumer {
    employment: IEmployment;
    endDate: string | Date | null;
    id: string;
    service: IService;
    startDate: string | Date;
}

interface IPractitioner {
    id: string;
    nameFamily: string;
    nameGiven: string | null;
    nameInfix: string | null;
}

interface IOrganization {
    id: string;
    name: string;
}

interface IEmployment {
    organization: IOrganization;
    practitioner: IPractitioner;
}

export function OrganizationServicesConsumingList(props: IServicesConsumingListProps) {
    const [serviceConsumers, setServiceConsumers] = useState<IServiceConsumer[]>([]);

    useEffect(() => {
        loadServiceConsumers(props.id);
    }, [props.id]);

    return (
        <>
            <Header content={translateKey('practitioner.consumingServices')} />
            <AdminTable>
                <TableHead>
                    <TableRow>
                        <TableHeader>{translateKey('practitioner.practitioner')}</TableHeader>
                        <TableHeader>{translateKey('common.name')}</TableHeader>
                        <TableHeader>{translateKey('common.type')}</TableHeader>
                        <TableHeader>{translateKey('common.startDate')}</TableHeader>
                        <TableHeader>{translateKey('common.endDate')}</TableHeader>
                        <TableHeader align="right"></TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {serviceConsumers.map((item) => (
                        <TableRow key={item.id}>
                            <TableData>
                                <Link
                                    to={`/practitioners/${item.employment.practitioner.id}/?tab=servicesConsumingListIsVisible`}
                                >
                                    <FullName
                                        nameFamily={item.employment.practitioner.nameFamily}
                                        nameGiven={item.employment.practitioner.nameGiven}
                                        nameInfix={item.employment.practitioner.nameInfix}
                                    />
                                </Link>
                            </TableData>
                            <TableData>{item.service.name}</TableData>
                            <TableData>{translateKey(`service.type_${item.service.serviceType}`)}</TableData>
                            <TableData>
                                <NullableFormattedDate value={item.startDate} />
                            </TableData>
                            <TableData>
                                <NullableFormattedDate value={item.endDate} />
                            </TableData>
                            <TableData>
                                <EditButton
                                    to={`/serviceConsumers/${item.id}?practitionerId=${item.employment.practitioner.id}`}
                                />
                            </TableData>
                        </TableRow>
                    ))}
                </TableBody>
            </AdminTable>
        </>
    );

    async function loadServiceConsumers(organizationId: string) {
        const url = '/api/serviceConsumers/?organizationId=' + organizationId;
        const result = await fetchJson(url, 'GET');

        setServiceConsumers(result.json.items);
    }
}
